import React, { useEffect, useState } from 'react';
import { Box, Button } from '@mui/material';
import { StatusCodes } from 'http-status-codes';
import StyledBalanceTableArea from './BalanceTableAreaAdmin.styles';
import Table from '../Table';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import Pagination from '../Pagination';
import { changeUserFilter } from '../../store/user/userSlice';
import userThunk from '../../store/user/userThunk';
import { adminPaymentTableHead } from '../../store/payment/adminPaymentTableHead';
import SearchInput from '../SearchInput/SearchInput';
import useDebounce from '../../utils/useDebounce';
import CSVExporter from '../../utils/csvExporter';
import { type PaymentExportType } from '../../types/exportTypes';
import { adminAPI } from '../../api/services/userApi';
import { formatDate } from '../../utils/dateFormater';
import { paymentsFileExportHeaders } from '../../utils/fileExportHeaders';

const BalanceTableAreaAdmin = () => {
  const userSlice = useAppSelector((state) => state.userSlice);
  const filter = useAppSelector((state) => state.userSlice.filter);
  const [search, setSearch] = useState('');
  const dispatch = useAppDispatch();
  const currentSearch = useDebounce(search);
  const onChangeFilter = (title: string, value: string | Date | null) => {
    dispatch(changeUserFilter({ title, value }));
  };

  const handleChangePage = (page: number) => {
    dispatch(changeUserFilter({ title: 'page', value: `${page}` }));
  };

  const handleChangeSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value);
  };

  useEffect(() => {
    (async () => {
      try {
        await dispatch(userThunk.getPayments(filter)).unwrap();
      } catch (error) {
        console.error(error);
      }
    })();
  }, [dispatch, filter]);

  useEffect(() => {
    dispatch(changeUserFilter({ title: 'findString', value: currentSearch }));
  }, [currentSearch, dispatch]);

  const onExportPayments = async (): Promise<PaymentExportType[]> => {
    try {
      const result = await adminAPI.getAllPayments();
      if (result.status === StatusCodes.OK) {
        const formattedData: PaymentExportType[] = result?.data?.data?.files?.map((payment) => ({
          ...payment,
          createdAt: formatDate(payment.createdAt),
          amount: payment?.amount.toString(),
        })) || [];
        return formattedData;
      }
      return [];
    } catch (error) {
      console.error(error);
      return [];
    }
  };

  return (
    <StyledBalanceTableArea>
      <Box sx={{
        marginTop: 5,
      }}
      >
        <Box sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 1,
        }}
        >
          <Box sx={{ width: { xs: '100%', lg: '50%' } }}>
            <SearchInput onChange={handleChangeSearch} value={search} nameInput="search" placeHolder="Search" />
          </Box>
          <CSVExporter<PaymentExportType>
            onClick={onExportPayments}
            headers={paymentsFileExportHeaders}
            filename="payments.csv"
            label={(
              <Button
                sx={{
                  px: 4,
                  py: 1.5,
                  borderRadius: 2,
                  textTransform: 'capitalize',
                }}
                variant="contained"
              >
                Export Payments
              </Button>
            )}
          />
        </Box>
      </Box>
      {
        userSlice.addition.isLoading ? (
          <div className="styled-app__loader-mini" />
        ) : (
          <>
            <Table
              filter={filter}
              setFilter={onChangeFilter}
              mainArray={userSlice.payments}
              tableHead={adminPaymentTableHead}
            />
            {
              userSlice?.options ? (
                <Pagination
                  currentPage={userSlice?.options.page}
                  totalCount={userSlice?.options.totalPages}
                  pageSize={userSlice?.options.perPage}
                  onPageChange={handleChangePage}
                  countItems={userSlice?.options.totalRecords}
                />
              ) : null
            }
          </>
        )
      }
    </StyledBalanceTableArea>
  );
};

export default BalanceTableAreaAdmin;
